class GroupedProductCard extends HTMLElement {
  constructor() {
    super();
    this.currentProductData = null;
    this.productDataCache = new Map();
    this.init();
  }

  init() {
    this.cacheProductData();
    this.sortVariantOptions(); // Sort variant options before binding events
    this.bindEvents();
    this.setInitialState();
  }

  sortVariantOptions() {
    // Sort variant option elements within each variant selector group
    for (let i = 1; i <= 3; i++) {
      const variantSelector = this.querySelector(`.product-variant-selector:nth-of-type(${i})`);
      if (!variantSelector) continue;
      
      const variantOptionsContainer = variantSelector.querySelector('.variant-options');
      if (!variantOptionsContainer) continue;
      
      const variantOptionElements = Array.from(variantOptionsContainer.querySelectorAll('.variant-option'));
      if (variantOptionElements.length <= 1) continue; // No need to sort if only one option
      
      // Sort the variant option elements based on their label text
      variantOptionElements.sort((a, b) => {
        const aLabel = a.querySelector('label')?.textContent?.trim() || '';
        const bLabel = b.querySelector('label')?.textContent?.trim() || '';
        
        return this.compareOptionValues(aLabel, bLabel);
      });
      
      // Clear the container and re-append in sorted order
      variantOptionsContainer.innerHTML = '';
      variantOptionElements.forEach(element => {
        variantOptionsContainer.appendChild(element);
      });
    }
  }

  compareOptionValues(a, b) {
    const aStr = String(a).trim();
    const bStr = String(b).trim();
    
    // Try to extract numeric value with units (e.g., "60K BTU", "2 Ton", "2.5 Ton")
    const aNumMatch = aStr.match(/^(\d+(?:\.\d+)?)\s*[KkTt]?\s*/);
    const bNumMatch = bStr.match(/^(\d+(?:\.\d+)?)\s*[KkTt]?\s*/);
    
    // Both have numeric values - sort numerically
    if (aNumMatch && bNumMatch) {
      const aNum = parseFloat(aNumMatch[1]);
      const bNum = parseFloat(bNumMatch[1]);
      return aNum - bNum;
    }
    
    // Try to extract percentage values (e.g., "80%", "95%")
    const aPercentMatch = aStr.match(/^(\d+(?:\.\d+)?)\s*%/);
    const bPercentMatch = bStr.match(/^(\d+(?:\.\d+)?)\s*%/);
    
    if (aPercentMatch && bPercentMatch) {
      const aNum = parseFloat(aPercentMatch[1]);
      const bNum = parseFloat(bPercentMatch[1]);
      return aNum - bNum;
    }
    
    // Try pure numeric values (e.g., "1", "2", "3")
    const aNum = parseFloat(aStr);
    const bNum = parseFloat(bStr);
    
    if (!isNaN(aNum) && !isNaN(bNum)) {
      return aNum - bNum;
    }
    
    // Fall back to alphabetical sorting for text values
    return aStr.localeCompare(bStr);
  }

  cacheProductData() {
    // Cache product data from script tag
    const productDataScript = this.querySelector('script[type="application/json"]');
    if (productDataScript) {
      try {
        const allProductData = JSON.parse(productDataScript.textContent);
        
        allProductData.forEach(productData => {
          this.productDataCache.set(productData.id, productData);
        });
        
        this.currentProductData = allProductData[0]; // Set first product as initial
      } catch (error) {
        console.error('Error parsing product data:', error);
      }
    }
  }

  bindEvents() {
    // Bind variant selector events with more specific targeting
    const variantSelectors = this.querySelectorAll('input[name^="variant-selector-"]');
    variantSelectors.forEach(selector => {
      selector.addEventListener('change', this.handleVariantChange.bind(this));
    });
    
    // Bind product link clicks
    const productLinks = this.querySelectorAll('[data-product-link]');
    productLinks.forEach(link => {
      link.addEventListener('click', this.handleProductLinkClick.bind(this));
    });
    
    // Bind product image clicks
    const productImages = this.querySelectorAll('.card__inner > .card__content');
    productImages.forEach(img => {
      img.style.cursor = 'pointer';
      img.addEventListener('click', this.handleProductLinkClick.bind(this));
    });
  }

  setInitialState() {
    if (this.currentProductData) {
      const currentSelections = this.getCurrentSelections();
      if (Object.keys(currentSelections).length > 0) {
        const matchingProduct = this.findMatchingProduct(currentSelections, 1);
        if (matchingProduct && matchingProduct.id !== this.currentProductData.id) {
          this.currentProductData = matchingProduct;
        }
      }
      this.updateProductInfo(this.currentProductData);
      this.updateVariantSelectors(this.currentProductData);
      this.updateVariantAvailability();
    }
  }

  handleVariantChange(event) {
    // Check if the event target belongs to this specific card instance
    if (!this.contains(event.target)) {
      return;
    }
    
    // Determine which metafield selector was changed
    const changedMetafieldNumber = this.getChangedMetafieldNumber(event.target.name);
    
    // Get all current selections for this card by metafield type
    const currentSelections = this.getCurrentSelections();
    
    // Find product that matches with hierarchical logic
    const matchingProduct = this.findMatchingProduct(currentSelections, changedMetafieldNumber);
    
    if (matchingProduct) {
      this.currentProductData = matchingProduct;
      this.updateProductInfo(matchingProduct);
      this.updateProductLinks(matchingProduct.url);
      this.updateVariantSelectors(matchingProduct);
    } else {
      // Keep current product, just update selectors to reflect it
      this.updateVariantSelectors(this.currentProductData);
    }
    
    // Always update availability after selection change
    this.updateVariantAvailability();
  }

  getChangedMetafieldNumber(inputName) {
    // Extract metafield number from name pattern: variant-selector-{number}-{product_id}
    const match = inputName.match(/variant-selector-(\d+)-/);
    return match ? parseInt(match[1]) : 1;
  }

  getCurrentSelections() {
    const selections = {};
    const allSelectors = this.querySelectorAll('input[name^="variant-selector-"]:checked');
    allSelectors.forEach(selector => {
      const match = selector.name.match(/variant-selector-(\d+)-/);
      if (match) {
        const metafieldNumber = match[1];
        const label = selector.nextElementSibling?.textContent?.trim() || '';
        selections[`metafield_${metafieldNumber}`] = label;
      }
    });
    return selections;
  }

  findMatchingProduct(targetSelections, changedMetafieldNumber = 1) {
    let bestMatch = null;
    let bestMatchScore = -1;
    
    this.productDataCache.forEach((product, id) => {
      let isMatch = true;
      let matchScore = 0;
      
      // Hierarchical matching logic based on which metafield changed
      // Priority: metafield_1 > metafield_2 > metafield_3
      
      const target1 = targetSelections.metafield_1;
      const target2 = targetSelections.metafield_2;
      const target3 = targetSelections.metafield_3;
      
      const product1 = product.metafield_1;
      const product2 = product.metafield_2;
      const product3 = product.metafield_3;
      
      // Metafield 1 matching (highest priority - always required if set)
      if (target1) {
        if (product1 === target1) {
          matchScore += 100; // Highest weight
        } else {
          isMatch = false; // metafield_1 mismatch = not a match
          return;
        }
      }
      
      // Metafield 2 matching (medium priority)
      if (target2) {
        if (changedMetafieldNumber === 1) {
          // Metafield 1 changed: prefer matching metafield_2 but don't require it
          if (product2 === target2) {
            matchScore += 10;
          }
        } else {
          // Metafield 2 or 3 changed: require metafield_2 match if we're past metafield 1
          if (product2 === target2) {
            matchScore += 10;
          } else if (changedMetafieldNumber >= 2) {
            isMatch = false; // metafield_2 mismatch when it should be locked
            return;
          }
        }
      }
      
      // Metafield 3 matching (lowest priority)
      if (target3) {
        if (changedMetafieldNumber <= 2) {
          // Metafield 1 or 2 changed: prefer matching metafield_3 but don't require it
          if (product3 === target3) {
            matchScore += 1;
          }
        } else {
          // Metafield 3 changed: require metafield_3 match
          if (product3 === target3) {
            matchScore += 1;
          } else {
            isMatch = false; // metafield_3 mismatch when it should be the selection
            return;
          }
        }
      }
      
      // Update best match if this product is better
      if (isMatch && matchScore > bestMatchScore) {
        bestMatch = product;
        bestMatchScore = matchScore;
      }
    });
    
    return bestMatch;
  }

  updateVariantSelectors(productData) {
    ['metafield_1', 'metafield_2', 'metafield_3'].forEach((metafieldKey, index) => {
      const metafieldNumber = index + 1;
      const productValue = productData[metafieldKey];
      const productValueNorm = productValue ? String(productValue).trim() : '';
      const selectors = this.querySelectorAll(`input[name^="variant-selector-${metafieldNumber}-"]`);
      selectors.forEach(selector => {
        const label = selector.nextElementSibling?.textContent?.trim() || '';
        if (productValueNorm && label === productValueNorm) {
          selector.checked = true;
        } else {
          selector.checked = false;
        }
      });
    });
  }

  handleProductLinkClick(event) {
    // Update href with current product URL before navigation
    if (this.currentProductData) {
      if (event.currentTarget.href !== undefined) {
        // It's a link element
        event.currentTarget.href = this.currentProductData.url;
      } else {
        // It's an image element, navigate directly
        window.location.href = this.currentProductData.url;
      }
    }
  }

  updateProductInfo(productData) {
    const dynamicInfo = this.querySelector('.dynamic-product-info');
    if (!dynamicInfo) {
      return;
    }

    // Add updating class for animation
    dynamicInfo.classList.add('updating');

    setTimeout(() => {
      // Update product title
      const titleElements = this.querySelectorAll('[data-product-title]');
      titleElements.forEach(el => {
        el.textContent = productData.title;
      });

      // Update product price - create simple price HTML
      const priceElements = this.querySelectorAll('[data-product-price]');
      priceElements.forEach(el => {
        // Get theme strings with fallbacks
        const strings = window.groupedProductCardStrings || {};
        const regularPriceLabel = strings.regularPrice || 'Regular price';
        const salePriceLabel = strings.salePrice || 'Sale price';
        
        let priceHtml = '';
        if (productData.compare_at_price > productData.price) {
          priceHtml = `
            <div class="price price--on-sale">
              <div class="price__container">
                <dl class="price__list">
                  <div class="price__item price__item--sale">
                    <dt class="visually-hidden">${salePriceLabel}</dt>
                    <dd class="price__sale">
                      <s class="price__compare">${productData.compare_at_price_formatted}</s>
                      <span class="price__current">${productData.price_formatted}</span>
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
          `;
        } else {
          priceHtml = `
            <div class="price">
              <div class="price__container">
                <dl class="price__list">
                  <div class="price__item price__item--regular">
                    <dt class="visually-hidden">${regularPriceLabel}</dt>
                    <dd>
                      <span class="price__current">${productData.price_formatted}</span>
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
          `;
        }
        el.innerHTML = priceHtml;
      });

      // Update product image
      const imageElements = this.querySelectorAll('[data-product-image]');
      imageElements.forEach(img => {
        if (productData.featured_image) {
          img.src = productData.featured_image.url;
          img.alt = productData.featured_image.alt || productData.title;
          // Generate srcset dynamically
          const baseUrl = productData.featured_image.url.split('?')[0];
          img.srcset = [
            `${baseUrl}?width=165 165w`,
            `${baseUrl}?width=360 360w`,
            `${baseUrl}?width=533 533w`,
            `${baseUrl}?width=720 720w`,
            `${baseUrl}?width=940 940w`,
            `${baseUrl}?width=1066 1066w`
          ].join(', ');
        }
      });

      // Update secondary image if exists
      const secondaryImages = this.querySelectorAll('[data-product-secondary-image]');
      secondaryImages.forEach(img => {
        if (productData.secondary_image) {
          img.src = productData.secondary_image.url;
          img.alt = productData.secondary_image.alt || productData.title;
          const baseUrl = productData.secondary_image.url.split('?')[0];
          img.srcset = [
            `${baseUrl}?width=165 165w`,
            `${baseUrl}?width=360 360w`,
            `${baseUrl}?width=533 533w`,
            `${baseUrl}?width=720 720w`,
            `${baseUrl}?width=940 940w`,
            `${baseUrl}?width=1066 1066w`
          ].join(', ');
        }
      });

      // Update Shopper Approved Category Stars Container 
      const saCatElements = this.querySelectorAll(".star_container");
      saCatElements.forEach(el => {
        const newEl = document.createElement("div");
        newEl.className = `star_container ${productData.variant_id}`;

        el.replaceWith(newEl);
        saLoadScript("https://www.shopperapproved.com/widgets/group2.0/42347.js");
      });

      // Update model/SKU
      const modelElements = this.querySelectorAll('[data-product-model]');
      modelElements.forEach(el => {
        el.textContent = productData.sku || '';
      });

      // Update vendor if shown
      const vendorElements = this.querySelectorAll('[data-product-vendor]');
      vendorElements.forEach(el => {
        el.textContent = productData.vendor || '';
      });

      // Update availability badge
      this.updateAvailabilityBadge(productData);

      // Update quick add forms
      this.updateQuickAddForms(productData);

      // Remove updating class and add updated class for animation
      dynamicInfo.classList.remove('updating');
      dynamicInfo.classList.add('updated');
      
      setTimeout(() => {
        dynamicInfo.classList.remove('updated');
      }, 300);
    }, 150);
  }

  updateProductLinks(productUrl) {
    const productLinks = this.querySelectorAll('[data-product-link]');
    productLinks.forEach(link => {
      link.href = productUrl;
    });
  }

  updateAvailabilityBadge(productData) {
    const badges = this.querySelectorAll('.card__badge span');
    badges.forEach(badge => {
      if (!productData.available) {
        badge.textContent = 'Sold out';
        badge.className = 'badge badge--bottom-left color-' + (window.badgeSettings?.soldOutScheme || 'accent-2');
      } else if (productData.compare_at_price > productData.price) {
        badge.textContent = 'Sale';
        badge.className = 'badge badge--bottom-left color-' + (window.badgeSettings?.saleScheme || 'accent-1');
      } else {
        badge.style.display = 'none';
      }
    });
  }

  updateQuickAddForms(productData) {
    // Get theme strings with fallbacks
    const strings = window.groupedProductCardStrings || {};
    const addToCartText = strings.addToCart || 'Add to cart';
    const soldOutText = strings.soldOut || 'Sold out';
    
    // Update hidden variant ID inputs
    const variantInputs = this.querySelectorAll('.product-variant-id');
    variantInputs.forEach(input => {
      input.value = productData.variant_id;
      input.disabled = !productData.available;
    });

    // Update quick add buttons
    const quickAddButtons = this.querySelectorAll('.quick-add__submit');
    quickAddButtons.forEach(button => {
      if (!productData.available) {
        button.disabled = true;
        const span = button.querySelector('span');
        if (span) span.textContent = soldOutText;
      } else {
        button.disabled = false;
        const span = button.querySelector('span');
        if (span) span.textContent = addToCartText;
      }
    });

    // Update modal openers with new product URL
    const modalOpeners = this.querySelectorAll('modal-opener button');
    modalOpeners.forEach(button => {
      button.setAttribute('data-product-url', productData.url);
    });
  }

  updateVariantAvailability() {
    // Get current selections to check against
    const currentSelections = this.getCurrentSelections();
    
    // Check availability for each metafield selector
    this.checkMetafieldAvailability(1, currentSelections);
    this.checkMetafieldAvailability(2, currentSelections);
    this.checkMetafieldAvailability(3, currentSelections);
  }

  checkMetafieldAvailability(metafieldNumber, currentSelections) {
    const selectors = this.querySelectorAll(`input[name^="variant-selector-${metafieldNumber}-"]`);
    
    selectors.forEach(selector => {
      const optionValue = selector.nextElementSibling?.textContent?.trim();
      if (!optionValue) return;
      
      // Create hypothetical selection with this option
      const testSelections = { ...currentSelections };
      testSelections[`metafield_${metafieldNumber}`] = optionValue;
      
      // Check if ANY product matches this combination with hierarchical logic
      const hasMatch = this.findMatchingProduct(testSelections, metafieldNumber);
      
      const option = selector.closest('.variant-option');
      if (option) {
        if (hasMatch) {
          option.classList.remove('unavailable');
          selector.disabled = false;
        } else {
          option.classList.add('unavailable');
          selector.disabled = true;
          // If this option is currently selected but unavailable, uncheck it
          if (selector.checked) {
            selector.checked = false;
          }
        }
      }
    });
  }

  connectedCallback() {
    // Update availability when component is added to DOM
    // This will be called after initial setup, so use the new dynamic check
    if (this.currentProductData) {
      this.updateVariantAvailability();
    }
  }
}

// Register the custom element
if (!customElements.get('grouped-product-card')) {
  customElements.define('grouped-product-card', GroupedProductCard);
}

// Initialize existing grouped product cards
document.addEventListener('DOMContentLoaded', () => {
  const groupedCards = document.querySelectorAll('grouped-product-card');
  groupedCards.forEach(card => {
    if (!card.initialized) {
      card.initialized = true;
    }
  });
});